import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { mq } from '../../../../../../common/src/utils/styledComponentsUtils';
import { object } from 'yup';
import {
  string,
  number,
} from '../../../../../../common/src/constants/validationSchemas';

// Components
import SearchSelectInputV2 from '../../../../../../common/src/components/SearchSelectInputV2';
import { NumbersInputWithSymbol } from 'wg-fe-ui';
import ActionButtons from './ActionButtons';

// Types
import { Annex } from '../../../../../../../types/SMT';
import { Change } from '../../../../../../../types/Form';
import { Box } from '@wegroup/design-system';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

interface Props {
  annex?: Annex;
  isMobile?: boolean;
  handleSave: (arg0: Annex) => void;
  handleRemove?: () => void;
  handleCancel: () => void;
}

const AnnexForm: React.FC<React.PropsWithChildren<Props>> = ({
  isMobile,
  annex,
  handleSave,
  handleRemove,
  handleCancel,
}) => {
  const { t } = useTranslation();

  const schema = object().shape({
    type: string.required,
    surface: number.required.moreThan(0, t('Value must be greater than 0')),
  });

  const { handleSubmit, control } = useForm<{ type: string; surface: number }>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...(annex?.type && {
        type: annex?.type,
      }),
      ...(annex?.surface && {
        surface: annex?.surface,
      }),
    },
  });

  const annexTypes = [
    { value: 'GARDEN_SHED', label: t('GARDEN_SHED') },
    { value: 'CARPORT', label: t('CARPORT') },
    { value: 'GAZEBO', label: t('GAZEBO') },
    { value: 'OTHER', label: t('OTHER') },
  ];

  return (
    <Box bg={{ base: 'white', laptop: 'unset' }} borderRadius="6px">
      <Wrapper data-test-id="ProdFac_AnnexForm">
        <Label>{t('Annex type')}</Label>

        <Controller
          name="type"
          control={control}
          render={({
            field: { onChange, value, name },
            fieldState: { error },
          }) => (
            <StyledSearchSelectInputV2
              name={name}
              isClearable
              onSelected={({ value }) => onChange(value)}
              options={annexTypes}
              value={annexTypes.find(({ value: _value }) => _value === value)}
              error={error?.message}
              placeholder={t('Annex type')}
              dataTestId="ProdFac_AnnexForm_BuildingTypeInput"
            />
          )}
        />
        <Controller
          name="surface"
          control={control}
          render={({
            field: { onChange, value, name },
            fieldState: { error },
          }) => (
            <StyledNumbersInputWithSymbol
              name={name}
              onChange={({ value }: Change) => onChange(Number(value))}
              type="number"
              value={value}
              error={error?.message}
              icon="m²"
              data-test-id="ProdFac_AnnexForm_GroundSurfaceInput"
            >
              {t('Ground surface area')}
            </StyledNumbersInputWithSymbol>
          )}
        />
      </Wrapper>
      <ActionButtons
        handleSubmit={handleSubmit(handleSave)}
        handleRemove={handleRemove}
        handleCancel={handleCancel}
        isMobile={isMobile}
      />
    </Box>
  );
};

const StyledSearchSelectInputV2 = styled(SearchSelectInputV2)`
  > label > p {
    font-size: 1.2rem;
  }
`;

const StyledNumbersInputWithSymbol = styled(NumbersInputWithSymbol)`
  height: unset;
  margin-top: 1.6rem;
  label {
    font-weight: 500;
    font-size: 1.6rem;
    color: black;
  }

  > div {
    margin-top: 0.4rem;
    margin-bottom: 0;
  }

  > label > span {
    padding: 1rem;
    top: 2.75rem;
  }
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.8rem;
`;

const Wrapper = styled.div`
  padding: 1.2rem;

  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #fff;
  border-radius: 6px;

  ${mq.laptop} {
    height: 20rem;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04);
  }
`;

export default AnnexForm;
